/**
 * The settings page's write path to the config surface
 * (`PUT /ops/config` — see .superpowers/sdd/2026-08-05-communicator-phase-1/
 * task-3-report.md). `../../lib/ops.ts`'s `ops.config()` already covers the
 * read half (`GET /ops/config`) and is reused directly by SettingsPage; it
 * has no write method, and this task's scope is
 * apps/console/src/components/settings/** plus its own test file (other
 * agents are concurrently working in components/modals/, apps/client/ and
 * services/ops/), so the write path is added here rather than by editing
 * lib/ops.ts. This mirrors ../context/aiOps.ts's own precedent: its own
 * small `createXClient({fetchImpl, baseUrl})` factory, injectable for
 * tests, resolved from runtime-config when not overridden, reusing
 * `OpsError`/`GATE_CODES` from lib/ops.ts and @communicator/shared so a
 * refused write is indistinguishable in shape from any other layer refusal
 * the console already knows how to render.
 */
import { GATE_CODES } from '@communicator/shared'
import { OpsError } from '../../lib/ops'
import { loadRuntimeConfig } from '../../runtime-config'

interface ConfigWriteDeps {
  /** Defaults to the global `fetch`. Override in tests. */
  fetchImpl?: typeof fetch
  /** Defaults to reading /runtime-config.json via loadRuntimeConfig(). Pass a literal (or a function
   * returning one) in tests to skip that fetch entirely — mirrors ../../lib/ops.ts's own deps shape. */
  baseUrl?: string | (() => string | Promise<string>)
}

const GATE_CODE_SET: ReadonlySet<string> = new Set(GATE_CODES)

function isGateCode(value: unknown): value is (typeof GATE_CODES)[number] {
  return typeof value === 'string' && GATE_CODE_SET.has(value)
}

async function refusalFor(res: Response): Promise<OpsError | Error> {
  const body = (await res.json().catch(() => null)) as { error?: { code?: unknown; message?: unknown } } | null
  const code = body?.error?.code
  const message =
    typeof body?.error?.message === 'string' ? body.error.message : `Request failed with status ${res.status}`
  return isGateCode(code) ? new OpsError(code, message) : new Error(message)
}

/**
 * `write()` never invents its own refusal copy — task-18 requirement #4:
 * "the layer, not this page, is the enforcement point." A refused write
 * throws the layer's own `OpsError` (code + message straight off the 409
 * body); anything else (network failure, a genuine 500) throws a plain
 * `Error` so a caller can tell "the layer said no on purpose" apart from
 * "something broke."
 */
export function createConfigWriteClient(deps: ConfigWriteDeps = {}) {
  const doFetch: typeof fetch = deps.fetchImpl ?? ((...args) => fetch(...args))

  async function resolveBase(): Promise<string> {
    if (deps.baseUrl !== undefined) {
      return typeof deps.baseUrl === 'function' ? deps.baseUrl() : deps.baseUrl
    }
    return (await loadRuntimeConfig()).apiBase
  }

  async function write(id: string, value: unknown): Promise<void> {
    const base = await resolveBase()
    const res = await doFetch(`${base}/ops/config`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id, value }),
    })
    if (!res.ok) throw await refusalFor(res)
  }

  return { write }
}

/** The singleton every real caller (SettingsPage) uses — same pattern as `ops` in ../../lib/ops.ts. */
export const configWrite = createConfigWriteClient()
